import { useQuery, useQueryClient } from '@tanstack/vue-query'
import type { QueryFunction } from '@tanstack/vue-query'

export type ConfDataQueryKey = readonly ['conf-data', string, object]

export function createConfDataQueryKey(url: URL): ConfDataQueryKey {
  return [
    'conf-data',
    getUrlWithoutProtocol(url),
    searchParamsToSortedObject(url.searchParams),
  ] as const
}

export function useConfDataQueryFn(): QueryFunction<
  ConfAPIPagesResponse,
  ConfDataQueryKey
> {
  const requestFetch = useRequestFetch()

  return ({ queryKey: [_, urlString, params], signal }) => {
    return requestFetch<ConfAPIPagesResponse>(
      `/conf-api/v1/pages/${urlString}`,
      { query: params, signal },
    )
  }
}

export function useConfDataQuery(
  url: MaybeRefOrGetter<URL>,
  options: {
    enabled?: MaybeRefOrGetter<boolean>
  } = {},
) {
  const queryFn = useConfDataQueryFn()

  return useQuery<
    ConfAPIPagesResponse,
    Error,
    ConfAPIPagesResponse,
    ConfDataQueryKey
  >({
    enabled: options.enabled,
    queryKey: computed(() => createConfDataQueryKey(toValue(url))),
    queryFn,
  })
}

const DEFAULT_MAX_CONF_REDIRECTS = 3

export function useLazyConfDataFetch(
  options: { followRedirect?: boolean; maxRedirects?: number } = {},
) {
  const client = useQueryClient()
  const queryFn = useConfDataQueryFn()
  const createConfDataUrl = useCreateConfDataURLFn()
  const { origin } = useRequestURL()

  return async (fullPath: string): Promise<ConfAPIPagesResponse> => {
    const maxRedirects = options.maxRedirects ?? DEFAULT_MAX_CONF_REDIRECTS
    let redirectCount = 0
    let url = createConfDataUrl(new URL(fullPath, origin))
    let response: ConfAPIPagesResponse

    do {
      response = await client.fetchQuery({
        queryKey: createConfDataQueryKey(toValue(url)),
        queryFn,
      })

      if (response.pagetype === 'redirect') {
        console.warn(
          `Redirect encountered while fetching conf data from ${url.toString()}`,
          `Redirecting to: ${response.body.location}`,
          response.body,
        )
      }

      if (
        options.followRedirect &&
        response.pagetype === 'redirect' &&
        (response.body as RedirectOut).location
      ) {
        url = createConfDataUrl(
          new URL((response.body as RedirectOut).location, origin),
        )
        redirectCount++
        if (redirectCount > maxRedirects) {
          throw new Error(
            `Maximum redirects (${maxRedirects}) exceeded for ConfDataFetch.\nInitial URL: ${fullPath}`,
          )
        }
      } else {
        break // Exit loop if not a redirect
      }
    } while (response.pagetype === 'redirect')

    return response
  }
}
